import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Pencil, Upload, Trash2, Stamp } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import { muze } from '../../lib/moduly';
import {
    SECTION_COLOR, CERVENA, nacti, posli, csrf, tlumene, cara,
    Nadpis, Pole, RadekPanelu, Tlacitko, PatickaModalu,
} from './ui';

/**
 * Správa → Firma. Identita firmy na jednom místě (název, sídlo, IČO, DIČ —
 * bere je účtenka), podpis a razítko zaměstnavatele (PDF smluv) a e-mail
 * účetní (uzávěrka docházky). Úprava v `ModalShell` (norma §1f: nastavení =
 * modal), třetí sloupec „Kde se používá" + „K dořešení".
 *
 * Dřív: Nastavení → Podpis & razítko (náhled z `/storage`, kam chráněný disk
 * nevede) a název/IČO/DIČ dvakrát — v provozních nákladech a v účtence.
 */
const OBRAZKY = [
    { klic: 'podpis_razitko_zamestnavatele', nazev: 'Podpis a razítko v jednom', popis: 'Má přednost — vloží se do dokumentu tak, jak je.' },
    { klic: 'podpis_zamestnavatele', nazev: 'Podpis', popis: 'Použije se, když chybí obrázek v jednom.' },
    { klic: 'razitko_zamestnavatele', nazev: 'Razítko', popis: 'Použije se, když chybí obrázek v jednom.' },
];

export function kDoreseniFirmy(data) {
    if (!data) return [];
    const u = data.udaje || {};
    const ulozeno = new Set(data.ulozeno || []);
    const o = data.obrazky || {};
    const out = [];
    if (!u.ico) out.push({ id: 'ico', problem: true, titulek: 'Chybí IČO', popis: 'Účtenka ho musí nést.' });
    if (!u.dic) out.push({ id: 'dic', problem: true, titulek: 'Chybí DIČ', popis: 'Zjednodušený daňový doklad musí nést DIČ prodejce.' });
    if (!o.podpis_razitko_zamestnavatele && !(o.podpis_zamestnavatele && o.razitko_zamestnavatele)) {
        out.push({ id: 'podpis', problem: true, titulek: 'Chybí podpis nebo razítko', popis: 'Smlouvy se nepodepíšou za zaměstnavatele.' });
    }
    if (u.sidlo && !ulozeno.has('sidlo')) out.push({ id: 'sidlo', problem: false, titulek: 'Sídlo zatím z adresy provozovny', popis: 'Ověř ho a ulož — pak se od provozovny oddělí.' });
    if (!data.email_ucetniho) out.push({ id: 'ucetni', problem: false, titulek: 'Chybí e-mail účetní', popis: 'Uzávěrka docházky nemá komu odejít.' });
    return out;
}

export default function SpravaFirma({ user, isDark, isMobile, cardStyle, inputStyle, rezervaDole }) {
    const upravuje = muze(user, 'sprava.firma', 'upravuje');
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [uprava, setUprava] = useState(false);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    useEffect(() => { nacti('/api/sprava/firma').then(setData).catch((e) => setChyba(e.message)); }, []);
    const kDoreseni = useMemo(() => kDoreseniFirmy(data), [data]);
    const ml = tlumene(isDark);
    const u = data?.udaje || {};

    const nahraj = async (klic, soubor) => {
        if (!soubor) return;
        setChyba(null);
        const fd = new FormData();
        fd.append('klic', klic);
        fd.append('soubor', soubor);
        const r = await fetch('/api/sprava/firma/obrazek', { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' }, body: fd, credentials: 'same-origin' });
        const d = await r.json().catch(() => null);
        if (r.ok) setData(d); else setChyba(d?.errors ? Object.values(d.errors).flat()[0] : d?.error || `Chyba ${r.status}`);
    };
    const smaz = async (o) => {
        if (!(await potvrdit(`Odebrat obrázek „${o.nazev}"?`, { potvrdit: 'Odebrat' }))) return;
        try { setData(await posli(`/api/sprava/firma/obrazek/${o.klic}`, 'DELETE')); } catch (e) { setChyba(e.message); }
    };

    const karta = (
        <div className={isMobile ? '' : 'rounded-xl'} style={{ ...(isMobile ? {} : cardStyle), padding: isMobile ? '4px 16px 16px' : '18px 22px', flex: 1, minWidth: 0 }}>
            {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '0 0 12px' }}>{chyba}</p>}

            <Nadpis isDark={isDark} vpravo={upravuje && !isMobile ? <Tlacitko isDark={isDark} Icon={Pencil} onClick={() => setUprava(true)}>Upravit</Tlacitko> : null}>
                Údaje firmy
            </Nadpis>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', columnGap: 24 }}>
                <Pole popisek="Název" isDark={isDark}>{u.nazev || null}</Pole>
                <Pole popisek="Sídlo" isDark={isDark}>{u.sidlo || null}</Pole>
                <Pole popisek="IČO" isDark={isDark}>{u.ico || null}</Pole>
                <Pole popisek="DIČ" isDark={isDark}>{u.dic || null}</Pole>
                <Pole popisek="E-mail na účtence" isDark={isDark}>{u.email || null}</Pole>
                <Pole popisek="Telefon na účtence" isDark={isDark}>{u.telefon || null}</Pole>
                <Pole popisek="E-mail účetní" isDark={isDark}>{data?.email_ucetniho || null}</Pole>
            </div>

            <Nadpis isDark={isDark} mt={24}>Podpis a razítko zaměstnavatele</Nadpis>
            <p className="text-xs" style={{ color: ml, margin: '0 0 10px' }}>Vkládají se do smluv a dokumentů k podpisu. PNG s průhledným pozadím vypadá nejlíp.</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                {OBRAZKY.map((o) => (
                    <Obrazek key={o.klic} o={o} stav={data?.obrazky?.[o.klic]} isDark={isDark} isMobile={isMobile} upravuje={upravuje}
                        onNahraj={(f) => nahraj(o.klic, f)} onSmaz={() => smaz(o)} />
                ))}
            </div>
            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
        </div>
    );

    return (
        <div className="flex" style={{ gap: 16, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
            {isMobile && kDoreseni.length > 0 && (
                <div style={{ padding: '8px 16px 0', width: '100%' }}>
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} />
                </div>
            )}
            {karta}
            {!isMobile && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: 16, width: 320, flexShrink: 0 }}>
                    <Nadpis isDark={isDark}>Kde se používá</Nadpis>
                    <RadekPanelu isDark={isDark} popis="Účtenka" hodnota="název, sídlo, IČO, DIČ" />
                    <RadekPanelu isDark={isDark} popis="Smlouvy" hodnota="podpis a razítko" />
                    <RadekPanelu isDark={isDark} popis="Uzávěrka docházky" hodnota="e-mail účetní" />
                    <Nadpis isDark={isDark} mt={16}>K dořešení</Nadpis>
                    <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne={data ? 'Firma je kompletní.' : 'Načítám…'} />
                </div>
            )}
            {uprava && data && (
                <UpravaFirmy data={data} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                    onClose={() => setUprava(false)} onUlozeno={(d) => { setData(d); setUprava(false); }} />
            )}
            {potvrzeni}
        </div>
    );
}

function Obrazek({ o, stav, isDark, isMobile, upravuje, onNahraj, onSmaz }) {
    const vstup = useRef(null);
    return (
        <div className="rounded-lg flex flex-col" style={{ border: `1px solid ${cara(isDark)}`, padding: 12, gap: 8 }}>
            <div>
                <p className="text-sm font-semibold" style={{ margin: 0, color: 'var(--text-primary)' }}>{o.nazev}</p>
                <p className="text-xs" style={{ margin: '2px 0 0', color: tlumene(isDark) }}>{o.popis}</p>
            </div>
            <div className="flex items-center justify-center rounded-md" style={{ height: 110, background: '#fff', border: `1px solid ${cara(isDark)}` }}>
                {stav
                    ? <img src={`/api/sprava/firma/obrazek/${o.klic}?v=${encodeURIComponent(stav.zmeneno)}`} alt={o.nazev} style={{ maxHeight: 100, maxWidth: '90%', objectFit: 'contain' }} />
                    : <span className="flex items-center gap-1.5 text-xs" style={{ color: '#888' }}><Stamp size={14} /> Nenahráno</span>}
            </div>
            {upravuje && (
                <div className="flex gap-2">
                    <input ref={vstup} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { onNahraj(e.target.files?.[0]); e.target.value = ''; }} />
                    <Tlacitko isDark={isDark} Icon={Upload} onClick={() => vstup.current?.click()}>{stav ? 'Nahradit' : 'Nahrát'}</Tlacitko>
                    {stav && !isMobile && <Tlacitko isDark={isDark} Icon={Trash2} onClick={onSmaz} title="Odebrat obrázek" />}
                </div>
            )}
        </div>
    );
}

function UpravaFirmy({ data, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [f, setF] = useState({ ...data.udaje, email_ucetniho: data.email_ucetniho || '' });
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const zmen = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
    const uloz = async () => {
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli('/api/sprava/firma', 'PUT', { ...f, email_ucetniho: f.email_ucetniho.trim() || null })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    const pole = (k, popisek, props = {}) => (
        <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{popisek}</span>
            <input value={f[k] ?? ''} onChange={zmen(k)} style={inputStyle} {...props} />
        </label>
    );
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Údaje firmy" ikona={Building2} barva={SECTION_COLOR} sirka={520}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                {pole('nazev', 'Název', { autoFocus: true, placeholder: 'FofrLama s.r.o.' })}
                {pole('sidlo', 'Sídlo', { placeholder: 'Ulice 1, 744 01 Město' })}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 12 }}>
                    {pole('ico', 'IČO', { inputMode: 'numeric', placeholder: '12345678' })}
                    {pole('dic', 'DIČ', { placeholder: 'CZ12345678' })}
                    {pole('email', 'E-mail na účtence', { type: 'email', placeholder: 'info@fofrlama.cz' })}
                    {pole('telefon', 'Telefon na účtence', { inputMode: 'tel', placeholder: '+420 604 400 663' })}
                </div>
                {pole('email_ucetniho', 'E-mail účetní (uzávěrka docházky)', { type: 'email' })}
                <p className="text-xs" style={{ color: tlumene(isDark), margin: 0 }}>Název, sídlo, IČO, DIČ, e-mail a telefon se hned propíšou na účtenky všech pokladen.</p>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}
